/* Announcement Bar Plus (ih-announcement-bar)
   Rotating announcement bar with fade or vertical-scroll transitions,
   optional navigation arrows, autoplay, and sessionStorage dismissal.
   Preserves the --announcement-bar-height root var used by the header layout. */
(function () {
  if (customElements.get('ih-announcement-bar')) return;

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  class IhAnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.transition = this.dataset.transition || 'fade';
      this.duration = parseInt(this.dataset.duration, 10) || 5000;
      this.closeable = this.dataset.closeable === 'true';
      this.sectionId = this.dataset.sectionId;
      this.storageKey = 'ih-ann-' + this.sectionId;

      this.index = 0;
      this.timer = null;
      this.visible = false;
      this.slideHeight = 0;
      this.resetting = false; // true while a scroll-wrap snap is pending
      this.clone = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.inner = this.querySelector('.ih-ann__inner');
      this.viewport = this.querySelector('.ih-ann__viewport');
      this.track = this.querySelector('.ih-ann__track');
      this.collectSlides();

      this.next = this.next.bind(this);
      this.prev = this.prev.bind(this);
      this.onResize = debounce(this.recalcHeight.bind(this), 100);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onEditorChange = this.onEditorChange.bind(this);
      this.hasSessionStorage = this.checkHasSessionStorage();
    }

    connectedCallback() {
      if (!this.slides.length) return;

      if (this.transition === 'scroll' && this.count > 1) this.setupScroll();
      this.bindControls();
      this.recalcHeight();

      // Theme-editor: re-init when this section's blocks change.
      document.addEventListener('shopify:section:load', this.onEditorChange);
      document.addEventListener('shopify:block:select', this.onEditorChange);
      document.addEventListener('shopify:block:deselect', this.onEditorChange);

      if (this.closeable && this.hasSessionStorage &&
          sessionStorage.getItem(this.storageKey) === 'hidden') {
        this.setHeightVar('0px');
        return; // stay dismissed — no resize listener needed
      }

      window.addEventListener('resize', this.onResize);
      window.setTimeout(this.show.bind(this), 500);
    }

    disconnectedCallback() {
      this.stop();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:section:load', this.onEditorChange);
      document.removeEventListener('shopify:block:select', this.onEditorChange);
      document.removeEventListener('shopify:block:deselect', this.onEditorChange);
    }

    collectSlides() {
      this.slides = Array.prototype.slice.call(
        this.querySelectorAll('.ih-ann__slide:not(.ih-ann__slide--clone)')
      );
      this.count = this.slides.length;
    }

    onEditorChange(e) {
      if (this.contains(e.target) || (e.target && e.target.contains && e.target.contains(this))) {
        this.reinit();
      }
    }

    reinit() {
      this.stop();
      if (this.clone) { this.clone.remove(); this.clone = null; }
      this.collectSlides();
      this.index = 0;
      this.resetting = false;
      this.track.style.transition = '';
      this.track.style.transform = '';
      this.slides.forEach(function (s, i) {
        s.classList.toggle('is-active', i === 0);
        if (i === 0) { s.removeAttribute('aria-hidden'); }
        else { s.setAttribute('aria-hidden', 'true'); }
      });
      if (this.transition === 'scroll' && this.count > 1) this.setupScroll();
      this.visible = true;
      this.recalcHeight();
      this.start();
    }

    setupScroll() {
      this.clone = this.slides[0].cloneNode(true);
      this.clone.classList.add('ih-ann__slide--clone');
      this.clone.classList.remove('is-active');
      this.clone.setAttribute('aria-hidden', 'true');
      this.track.appendChild(this.clone);
    }

    bindControls() {
      var self = this;
      var prevBtn = this.querySelector('.ih-ann__arrow--prev');
      var nextBtn = this.querySelector('.ih-ann__arrow--next');
      if (prevBtn) prevBtn.addEventListener('click', function () { self.prev(); self.start(); });
      if (nextBtn) nextBtn.addEventListener('click', function () { self.next(); self.start(); });

      var closeBtn = this.querySelector('.ih-ann__close');
      if (closeBtn) closeBtn.addEventListener('click', this.hide.bind(this));

      this.addEventListener('mouseenter', this.stop.bind(this));
      this.addEventListener('mouseleave', this.start.bind(this));
      this.addEventListener('focusin', this.stop.bind(this));
      // Only resume when focus actually leaves the bar, not when it moves
      // between child controls (WCAG 2.2.2 — don't advance while focused).
      this.addEventListener('focusout', this.onFocusOut);
    }

    onFocusOut(e) {
      if (!this.contains(e.relatedTarget)) this.start();
    }

    sizeScroll() {
      var max = 0;
      var all = this.clone ? this.slides.concat(this.clone) : this.slides;
      all.forEach(function (s) { s.style.height = 'auto'; });
      this.slides.forEach(function (s) { max = Math.max(max, s.scrollHeight); });
      if (this.clone) max = Math.max(max, this.clone.scrollHeight);
      this.slideHeight = max;
      this.viewport.style.height = max + 'px';
      all.forEach(function (s) { s.style.height = max + 'px'; });
      this.applyScroll(false);
    }

    applyScroll(animate) {
      if (!this.slideHeight) return;
      var y = -this.index * this.slideHeight;
      if (!animate || this.reducedMotion) {
        this.track.style.transition = 'none';
        this.track.style.transform = 'translateY(' + y + 'px)';
        void this.track.offsetHeight; // reflow so the jump commits with no animation
        this.track.style.transition = '';
      } else {
        this.track.style.transform = 'translateY(' + y + 'px)';
      }
    }

    recalcHeight() {
      if (this.transition === 'scroll' && this.count > 1) this.sizeScroll();
      if (this.visible && this.inner) {
        var h = this.inner.offsetHeight + 'px';
        this.style.maxHeight = h;
        this.setHeightVar(h);
      }
    }

    setHeightVar(h) {
      document.documentElement.style.setProperty('--announcement-bar-height', h);
    }

    show() {
      this.visible = true;
      this.recalcHeight();
      this.start();
    }

    hide() {
      if (this.closeable && this.hasSessionStorage) {
        sessionStorage.setItem(this.storageKey, 'hidden');
      }
      this.visible = false;
      this.style.maxHeight = '0px';
      this.setHeightVar('0px');
      this.stop();
    }

    next() {
      if (this.count < 2 || this.resetting) return;
      if (this.transition === 'fade') {
        this.fadeTo(this.index + 1);
      } else {
        this.scrollForward();
      }
    }

    prev() {
      if (this.count < 2 || this.resetting) return;
      if (this.transition === 'fade') {
        this.fadeTo(this.index - 1);
      } else {
        this.scrollBackward();
      }
    }

    fadeTo(i) {
      this.index = (i + this.count) % this.count;
      this.slides.forEach(function (s, idx) {
        var active = idx === this.index;
        s.classList.toggle('is-active', active);
        if (active) { s.removeAttribute('aria-hidden'); }
        else { s.setAttribute('aria-hidden', 'true'); }
      }, this);
    }

    scrollForward() {
      this.index += 1;
      this.applyScroll(true);
      if (this.index >= this.count) {
        // Landed on the appended clone of slide 0 — snap back to real 0 after the transition.
        this.resetting = true;
        var self = this;
        var reset = function () {
          self.index = 0;
          self.applyScroll(false);
          self.resetting = false;
          self.track.removeEventListener('transitionend', reset);
        };
        this.track.addEventListener('transitionend', reset);
      }
    }

    scrollBackward() {
      if (this.index > 0) {
        this.index -= 1;
        this.applyScroll(true);
        return;
      }
      // At slide 0: jump (no animation) to the end clone position, which looks
      // identical to slide 0, then animate UP to the last real slide — a
      // seamless backward wrap reusing the same end clone.
      var self = this;
      this.index = this.count;
      this.applyScroll(false);
      this.resetting = true;
      window.requestAnimationFrame(function () {
        self.index = self.count - 1;
        self.applyScroll(true);
        self.resetting = false;
      });
    }

    start() {
      this.stop();
      if (this.count > 1 && this.visible && !this.reducedMotion) {
        this.timer = setInterval(this.next, this.duration);
      }
    }

    stop() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }

    checkHasSessionStorage() {
      if (window.self !== window.top) return false;
      try {
        window.sessionStorage.setItem('test', '1');
        window.sessionStorage.removeItem('test');
        return true;
      } catch (e) {
        return false;
      }
    }
  }

  customElements.define('ih-announcement-bar', IhAnnouncementBar);
})();
